// apps/web/src/api/transport-types.ts — typed wrappers around
// `/api/transport-types/*`. Each entry routes through `request<T>` so
// the shared 401 retry / `ThrottleError` / `ApiClientError` surface is
// preserved.
import {
  GetTransportTypesResponseSchema,
  TransportTypeSchema,
  type CreateTransportTypeInput,
  type GetTransportTypesQuery,
  type GetTransportTypesResponse,
  type PatchTransportTypeInput,
  type TransportType,
} from '@magimp/shared';
import { request } from './client.js';

function withQuery(path: string, qs: string): string {
  return qs.length > 0 ? `${path}?${qs}` : path;
}

export function listTransportTypes(
  query: Partial<GetTransportTypesQuery> = {},
): Promise<GetTransportTypesResponse> {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.active !== undefined) params.set('active', String(query.active));
  if (query.category) params.set('category', query.category);
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  return request<GetTransportTypesResponse>(
    withQuery('/transport-types', params.toString()),
    { method: 'GET' },
    {},
    GetTransportTypesResponseSchema,
  );
}

export function fetchTransportType(id: string): Promise<TransportType> {
  return request<TransportType>(
    `/transport-types/${encodeURIComponent(id)}`,
    { method: 'GET' },
    {},
    TransportTypeSchema,
  );
}

export function createTransportType(body: CreateTransportTypeInput): Promise<TransportType> {
  return request<TransportType>(
    '/transport-types',
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    TransportTypeSchema,
  );
}

export function patchTransportType(
  id: string,
  body: PatchTransportTypeInput,
): Promise<TransportType> {
  return request<TransportType>(
    `/transport-types/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    TransportTypeSchema,
  );
}

export function archiveTransportType(id: string): Promise<TransportType> {
  return request<TransportType>(
    `/transport-types/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    {},
    TransportTypeSchema,
  );
}

export function activateTransportType(id: string): Promise<TransportType> {
  return request<TransportType>(
    `/transport-types/${encodeURIComponent(id)}/activate`,
    { method: 'POST' },
    {},
    TransportTypeSchema,
  );
}
